/**
 * Use to return a long date and short date of the local timezone at the time of request.
 *
 * @param locale string en-US
 * @example "en-US longDate" - April 17, 2024 at 2:41 PM EDT
 * @example "en-US shortDate" - Apr 17, 2024, 2:41 PM EDT
 * @example "it-IT longDate" - 17 aprile 2024 alle ore 14:40 GMT-4
 * @example "it-IT shortDate" - 17 apr 2024, 14:41 GMT-4
 */
export const getFormattedTimestamp = (locale: string) => {
  const createDates = (locale: string) => {
    const dateObject = new Date();
    const longDateFormatter = Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short",
    });
    const shortDateFormatter = Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short",
    });

    const longDate = longDateFormatter.format(dateObject);
    const shortDate = shortDateFormatter.format(dateObject);

    return { longDate, shortDate };
  };

  try {
    return createDates(locale);
  } catch (err) {
    console.warn("Invalid Locale Provided: Using default");
    return createDates("default");
  }
};

const periodToDate = (period: string) => {
  const year = parseInt(period.slice(0, 4), 10);
  const month = parseInt(period.slice(4, 6), 10) - 1;
  return new Date(year, month, 10);
};

/**
 * Use to return a Month Year date format.
 *
 * @param period number 202505
 * @param locale string en-US
 * @example "202505 en-US" - May 202505
 */
export const getMonthYearDateFormat = (period: number, locale: string) => {
  const createDates = (locale: string) => {
    const dateObject = periodToDate(String(period));
    const monthYearDateFormatter = Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric",
    });

    const monthYearDate = monthYearDateFormatter.format(dateObject);

    return monthYearDate;
  };

  try {
    return createDates(locale);
  } catch (err) {
    console.warn("Invalid Locale Provided: Using default");
    return createDates("default");
  }
};

/**
 * Use to return a big-endian date format (YYYY/MM/DD) with 12-hour time.
 *
 * @param locale string en-US
 * @example "en-US" - { time: "12:30am", date: "2025/08/29" }
 */
export const getBigEndianTimestamp = (locale: string) => {
  const dateObject = new Date();

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const time = timeFormatter.format(dateObject).toLowerCase();

  const year = dateObject.getFullYear();
  const month = String(dateObject.getMonth() + 1).padStart(2, "0");
  const day = String(dateObject.getDate()).padStart(2, "0");

  return { time, date: `${year}/${month}/${day}` };
};
